import { useCallback, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Link,
    Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { BudgetVersionType } from '@callmart/types';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import { useHEMTUsers } from '../../hooks/api/useHEMTUsers';
import { useDeleteBudgetScenario } from '../../hooks/api/budgetScenarios';
import { BudgetScenario } from '../../services/budgetScenario';
import { BudgetRoutes, budgetScenarioDetailRoute } from '../../enums/BudgetRoutes';
import ScenarioDuplicateDialog from './ScenarioDuplicateDialog';
import HintIcon from './HintIcon';
import { formatBudgetDate, SCENARIO_CLAMP_TWO_LINES, SCENARIO_GRID_CLASS, SCENARIO_ROW_HEIGHT } from './helpers';

interface ScenariosGridProps {
    scenarios: BudgetScenario[];
    isLoading: boolean;
}

const ScenariosGrid = ({ scenarios, isLoading }: ScenariosGridProps) => {
    const { t } = useTranslation('budget');
    const { canEdit, versions, buckets } = useBudgetContext();
    const navigate = useNavigate();
    // Resolve the creator id → display name (Bearbeiter). Falls back to '#id' if the directory is
    // unavailable; full multi-user resolution can move server-side (a creator join) in a later phase.
    const { data: users } = useHEMTUsers();

    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [duplicateScenario, setDuplicateScenario] = useState<BudgetScenario | null>(null);

    const { mutate: deleteScenario } = useDeleteBudgetScenario();

    const usersMap = useMemo(() => new Map(users.map((u) => [u.id, u.name])), [users]);
    // Keyed to the whole version rather than to its name: the Budget cell needs the version's bucket and
    // its snapshot flag out of the same lookup.
    const versionsMap = useMemo(() => new Map(versions.map((v) => [v.id, v])), [versions]);
    // The list endpoint joins the version but not its bucket, and the context already holds every bucket
    // (it is what the bucket selector is built from) — so the name is resolved here rather than server-side.
    const bucketsMap = useMemo(() => new Map(buckets.map((b) => [b.id, b.name])), [buckets]);

    const rowData = useMemo(
        () =>
            scenarios.map((scenario) => {
                // Prefer the version joined by the API; fall back to the context version list.
                const version = scenario.version ?? versionsMap.get(scenario.budgetVersionId);
                return {
                    ...scenario,
                    artLabel: t(`scenarioType.${scenario.type}`),
                    editorName:
                        scenario.createdBy != null
                            ? (usersMap.get(scenario.createdBy) ?? `#${scenario.createdBy}`)
                            : '',
                    // A version name is only unique INSIDE its bucket — two buckets can each hold a
                    // "Budget 7" — so the version alone does not say which budget the scenario belongs to.
                    // Kept as its own field, not glued into budgetName, because the two are rendered on
                    // two lines and the wrapped cell's `white-space: normal` collapses a "\n" to a space.
                    bucketName: version ? (bucketsMap.get(version.budgetBucketId) ?? '') : '',
                    // A released snapshot is marked: the scenario is then a what-if over a frozen baseline,
                    // which changes how its impact should be read.
                    budgetName: `${version?.name ?? ''}${
                        version?.type === BudgetVersionType.SNAPSHOT ? ` (${t('version.statusReleased')})` : ''
                    }`,
                };
            }),
        [scenarios, t, usersMap, versionsMap, bucketsMap]
    );

    const handleDeleteRow = useCallback((id: number) => setDeleteConfirmId(id), []);

    const handleConfirmDelete = useCallback(() => {
        if (deleteConfirmId !== null) {
            deleteScenario(deleteConfirmId);
            setDeleteConfirmId(null);
        }
    }, [deleteScenario, deleteConfirmId]);

    const columnDefs: ColDef[] = useMemo(
        () => [
            {
                headerName: t('field.scenarioName'),
                field: 'name',
                minWidth: 180,
                // A wrapped title still ends at the second line, so the full one stays one hover away —
                // the same pairing scenarioEntityColumn makes for the detail grids' label columns.
                tooltipValueGetter: (params) => String(params.value ?? ''),
                // Widen the click target to the whole cell — the link alone is easy to miss.
                cellStyle: { cursor: 'pointer' },
                onCellClicked: (event) => {
                    // The link handles its own clicks (and stays keyboard-focusable).
                    if ((event.event?.target as HTMLElement | null)?.closest('a,button')) return;
                    if (event.data) navigate(budgetScenarioDetailRoute(event.data.id));
                },
                // A real anchor with an href, not a styled button (CALL-794): the title is a link to a page,
                // so middle-click, ctrl/cmd-click and "open in new tab" have to work, and they cannot on a
                // <button> whose only navigation is an onClick. RouterLink still handles the plain click
                // in-app, so nothing reloads.
                cellRenderer: (params: ICellRendererParams) =>
                    params.data ? (
                        <Link
                            component={RouterLink}
                            to={budgetScenarioDetailRoute(params.data.id)}
                            underline="hover"
                            // The cell is a flex container (it centres the wrapped value), which would
                            // otherwise stretch the anchor and centre a second line of the title.
                            sx={{ textAlign: 'left', ...SCENARIO_CLAMP_TWO_LINES }}
                        >
                            {params.data.name}
                        </Link>
                    ) : null,
            },
            {
                headerName: t('field.description'),
                field: 'description',
                minWidth: 220,
                tooltipValueGetter: (params) => String(params.value ?? ''),
                // CALL-800: the descriptions are whole questions ("Was kostet eine höhere Erreichbarkeit
                // im IHD, wenn …"), and the row cut the third line through the middle of the letters.
                // The clamp ends the cell at two full lines with an ellipsis; the tooltip above carries
                // the rest. It needs this element of its own — see SCENARIO_CLAMP_TWO_LINES.
                cellRenderer: (params: ICellRendererParams) => (
                    <span style={SCENARIO_CLAMP_TWO_LINES}>{params.value}</span>
                ),
            },
            { headerName: t('field.scenarioType'), field: 'artLabel', minWidth: 170 },
            { headerName: t('field.editor'), field: 'editorName', minWidth: 150 },
            {
                headerName: t('field.budget'),
                field: 'budgetName',
                minWidth: 140,
                // Bucket over version, on the two lines the taller row provides. The bucket wears the same
                // filled chip with a tag icon that the version selector gives it (BudgetVersionSelector) —
                // it is the same fact in both places, and the chip is what makes it read as the version's
                // container rather than a second name.
                //
                // One wrapper element, because `.scenario-grid .ag-cell-wrap-text` centres the cell with
                // flex: two top-level children would become two flex items and land side by side instead
                // of stacking.
                cellRenderer: (params: ICellRendererParams) =>
                    params.data ? (
                        <div>
                            {params.data.bucketName && (
                                <Chip
                                    icon={<LabelOutlinedIcon />}
                                    label={params.data.bucketName}
                                    size="small"
                                    color="default"
                                    variant="filled"
                                    // A stock small Chip is 24px of mostly padding, which pushed the two
                                    // lines apart far more than the text needed. Trimmed to the height of
                                    // the line it sits above so the pair reads as one cell.
                                    sx={{
                                        height: 18,
                                        '& .MuiChip-label': { px: 0.5, fontSize: 11 },
                                        '& .MuiChip-icon': { fontSize: 13, ml: 0.5, mr: -0.25 },
                                    }}
                                />
                            )}
                            <div>{params.data.budgetName}</div>
                        </div>
                    ) : null,
                tooltipValueGetter: (params) => [params.data?.bucketName, params.value].filter(Boolean).join(' · '),
            },
            {
                headerName: t('field.startDate'),
                field: 'startDate',
                minWidth: 120,
                valueFormatter: (params) => formatBudgetDate(params.value),
            },
            {
                headerName: t('field.endDate'),
                field: 'endDate',
                minWidth: 120,
                valueFormatter: (params) => formatBudgetDate(params.value),
            },
            {
                headerName: '',
                field: 'actions',
                minWidth: canEdit ? 130 : 60,
                maxWidth: canEdit ? 130 : 60,
                sortable: false,
                filter: false,
                cellRenderer: (params: ICellRendererParams) => {
                    if (!params.data) return null;
                    return (
                        <>
                            {/* The board's separate view and edit icons collapse into one action: the
                                scenario page IS the editor, and it renders read-only for roles without
                                write access — so the icon states which of the two you get. */}
                            <IconButton
                                size="small"
                                aria-label={canEdit ? t('action.edit') : t('scenarios.open')}
                                title={canEdit ? t('action.edit') : t('scenarios.open')}
                                onClick={() => navigate(budgetScenarioDetailRoute(params.data.id))}
                            >
                                {canEdit ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                            {canEdit && (
                                <>
                                    <IconButton
                                        size="small"
                                        aria-label={t('scenarios.duplicate')}
                                        title={t('scenarios.duplicate')}
                                        onClick={() => setDuplicateScenario(params.data as BudgetScenario)}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        aria-label={t('action.delete')}
                                        title={t('action.delete')}
                                        onClick={() => handleDeleteRow(params.data.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </>
                            )}
                        </>
                    );
                },
            },
        ],
        [t, canEdit, navigate, handleDeleteRow]
    );

    // The overview is READ-ONLY: every value here is either metadata edited on the scenario page or joined
    // from another table. The shared wrapper defaults columns to `editable: true` and this grid registers no
    // onCellValueChanged, so without this a typed cell was accepted, kept nowhere and dropped on the next
    // render — an edit that looked like it saved.
    const defaultColDef: ColDef = useMemo(
        () => ({
            editable: false,
            sortable: true,
            filter: true,
            resizable: true,
            // CALL-794: on EVERY column, not just the two that need to wrap. `wrapText` is what puts the
            // .ag-cell-wrap-text class on a cell, and in App.css that class carries the vertical centring
            // as well as the wrap rules — ag-grid's own cell line-height is derived from the theme's
            // --ag-row-height (48px), which it does not update from the rowHeight option, so an unwrapped
            // cell on a taller row draws its value near the row's TOP edge. Leaving it off the short columns
            // would have centred Titel/Beschreibung/Budget and left Art, Bearbeiter, Start, Ende and the
            // action icons floating above them.
            //
            // Deliberately NOT `autoHeight`: it sizes each row to its own content, so a long description
            // would make one row twice its neighbours instead of clipping at the line the tooltip covers.
            wrapText: true,
        }),
        []
    );

    return (
        // The overview is a scenario grid like the detail ones: same row height, same wrap/centring CSS.
        // It needs no inline-editor rule of its own — everything here is read-only.
        <Box className={SCENARIO_GRID_CLASS}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        <span>{t('scenarios.title')}</span>
                    </Typography>
                    {/* CALL-800: behind the `?`, like every other budget grid's note — a sentence of
                        orientation read once was pushing the table down on every visit. */}
                    <HintIcon notes={t('hint.scenariosOverview')} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(BudgetRoutes.SCENARIOS_CONSOLIDATED)}
                    >
                        <span>{t('consolidated.openButton')}</span>
                    </Button>
                    {canEdit && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => navigate(BudgetRoutes.SCENARIO_NEW)}
                            disabled={versions.length === 0}
                        >
                            <span>{t('scenarios.add')}</span>
                        </Button>
                    )}
                </Box>
            </Box>
            <TableGridItem
                height="auto"
                domLayout="autoHeight"
                // The same height the scenario detail grids use — one constant, because the App.css wrap,
                // centring and editor rules are tied to it by hand (helpers.ts spells the coupling out).
                rowHeight={SCENARIO_ROW_HEIGHT}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={(params) => String(params.data.id)}
                loading={isLoading}
                cellSelection
                noRowsOverlayComponent={() => <span style={{ padding: 10 }}>{t('scenarios.empty')}</span>}
            />

            <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} maxWidth="sm">
                <DialogTitle>{t('scenarios.delete')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('dialog.deleteScenario')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmId(null)} variant="outlined">
                        {t('action.cancel')}
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">
                        {t('action.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Keyed so opening it for another row remounts it with that row's defaults. */}
            <ScenarioDuplicateDialog
                key={duplicateScenario?.id ?? 'none'}
                scenario={duplicateScenario}
                onClose={() => setDuplicateScenario(null)}
                onCreated={(id) => {
                    setDuplicateScenario(null);
                    navigate(budgetScenarioDetailRoute(id));
                }}
            />
        </Box>
    );
};

export default ScenariosGrid;
